import type { Ancestor } from "@/entities/ancestor";
import type { InterventionCreatePayload } from "@/entities/intervention";
import type { Recommendation } from "@/entities/recommendation";
import type { Nullable } from "@/types";

import { Skeleton } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { useCreate } from "@/hooks/queries";
import { useNavigateToRessource } from "@/hooks/useNavigateToRessource";
import { getAssetSelectOptionValue } from "@/modules/assets/assets-select-parent";
import { useInterventionTemplateValue } from "@/modules/intervention-template/intervention-template-select";
import { getLocationSelectOptionValue } from "@/modules/location/location-select-parent";
import { useOrganizationSelectOptionValue } from "@/modules/organization/organization-select";

import InterventionForm from "./InterventionForm";

const invalidateKeys = [["interventions", "get"]];

const Create = ({
  ancestors = [],
  max_days_delay,
  recommendation,
  redirectToModel = true,
}: {
  ancestors?: Ancestor[];
  max_days_delay?: Nullable<number>;
  recommendation?: Recommendation;
  redirectToModel?: boolean;
}) => {
  const { t } = useTranslation();
  const mutation = useCreate("interventions", { invalidateKeys });
  const saveButtonLabel = t("global:add");

  const onNavigateToIntervention =
    useNavigateToRessource<InterventionCreatePayload>(
      "/interventions/{{id}}",
      ({ data }) => {
        return data.data.id;
      }
    );

  const { organizationSelectValue, isLoading } =
    useOrganizationSelectOptionValue(ancestors);

  const {
    value: templateValue,
    defaultTemplate,
    isLoading: isLoadingTemplate,
    type: interventionType,
  } = useInterventionTemplateValue(ancestors);

  const initialValues = {
    organization: organizationSelectValue,
    location: getLocationSelectOptionValue(ancestors),
    asset: getAssetSelectOptionValue(ancestors),
    recommendation: {
      value: recommendation?.id,
      label: recommendation?.name,
      key: recommendation?.id,
    },
    application_date: dayjs(),
    scheduled_date: "",
    cost_type: "capex",
    max_days_delay,
    interventionType,
    template: templateValue,
  };

  if (isLoading || isLoadingTemplate) {
    return <Skeleton active />;
  }

  return (
    <InterventionForm
      mutation={mutation}
      saveButtonLabel={saveButtonLabel}
      initialValues={initialValues}
      defaultTemplate={defaultTemplate}
      onMutate={redirectToModel ? onNavigateToIntervention : undefined}
    />
  );
};

export default Create;
